import i18n from "i18next";
import { initReactI18next } from "react-i18next";

export const APP_LOCALES = ["en", "th"] as const;
export type AppLocale = (typeof APP_LOCALES)[number];

export const LOCALE_STORAGE_KEY = "smart-market.backoffice.locale";

const en = {
  common: {
    backoffice: "Backoffice",
    language: "Language",
    english: "English",
    thai: "ไทย",
    save: "Save",
    cancel: "Cancel",
    confirm: "Confirm",
    close: "Close",
    edit: "Edit",
    add: "Add",
    delete: "Delete",
    search: "Search",
    refresh: "Refresh",
    retry: "Try again",
    loading: "Loading…",
    dismiss: "Dismiss",
    showPassword: "Show password",
    hidePassword: "Hide password",
    previousPage: "Previous page",
    nextPage: "Next page",
    pagination: "Pagination",
    page: "Page {{page}}",
    pageOf: "Page {{page}} of {{total}}",
    showing: "Showing {{from}}–{{to}} of {{total}} {{items}}",
    items: "items",
    localDev: "Local dev",
    create: "Create",
    update: "Update",
    enabled: "Enabled",
    disabled: "Disabled",
    status: "Status",
    name: "Name",
    code: "Code",
    order: "Order",
    actions: "Actions",
    details: "Details",
    filters: "Filters",
    all: "All",
    yes: "Yes",
    no: "No",
    none: "None",
    copy: "Copy",
    copied: "Copied",
    reset: "Reset",
    apply: "Apply",
    clear: "Clear",
    itemLabels: {
      admins: "admins",
      categories: "categories",
      changes: "changes",
      comments: "comments",
      deposits: "deposits",
      disputes: "disputes",
      entries: "entries",
      events: "events",
      keys: "keys",
      leagues: "leagues",
      markets: "markets",
      members: "members",
      movements: "movements",
      orders: "orders",
      overrides: "overrides",
      positions: "positions",
      requests: "requests",
      sources: "sources",
      roles: "roles",
      trades: "trades",
      transactions: "transactions",
      versions: "versions",
      wallets: "wallets",
      withdrawals: "withdrawals",
    },
    sort: {
      asc: "ascending",
      desc: "descending",
      direction: "Sort direction {{direction}}",
      title: "Sort {{direction}}",
    },
    selectNamed: "Select {{name}}",
    selectAllOnPage: "Select all {{items}} on this page",
    selectedCount: "{{count}} selected",
    publishCount: "Publish {{count}} {{items}}?",
    unpublishCount: "Unpublish {{count}} {{items}}?",
    publishAll: "Publish all",
    unpublishAll: "Unpublish all",
    filterSummary: {
      allStatuses: "All statuses",
      openHolds: "Open holds",
      statusCount: "{{count}} statuses",
      stateCount: "{{count}} states",
      byStatus: "Filter by status — {{summary}}",
      byState: "Filter by state — {{summary}}",
    },
    counts: {
      activeLease_one: "{{count}} active lease",
      activeLease_other: "{{count}} active leases",
      instrument_one: "{{count}} instrument",
      instrument_other: "{{count}} instruments",
      coverage: "{{covered}} of {{total}} {{noun}} have a policy",
      paused: "{{count}} paused",
    },
    policyNouns: {
      enabledNetwork_one: "enabled network",
      enabledNetwork_other: "enabled networks",
      withdrawableAsset_one: "withdrawable asset",
      withdrawableAsset_other: "withdrawable assets",
      depositEnabledAsset_one: "deposit-enabled asset",
      depositEnabledAsset_other: "deposit-enabled assets",
    },
    noPolicy: "No policy: {{names}}",
  },
  auth: {
    operatorConsole: "Operator console",
    signIn: "Sign in",
    subtitle: "Use your assigned admin email.",
    email: "Email",
    password: "Password",
    signingIn: "Signing in",
    continue: "Continue",
    lostAccess: "Lost access?",
    contactAdmin: "Contact a SUPER_ADMIN",
    twoFactor: "Two-factor",
    enterCode: "Enter your code",
    emailCode: "Enter the 6-digit code we emailed to {{email}}.",
    authenticatorCode:
      "Open {{factor}} on {{email}}'s device and enter the current 6-digit code.",
    digit: "Digit {{digit}} of 6",
    verifying: "Verifying",
    verifyAndSignIn: "Verify & sign in",
    backToPassword: "← Back to password",
    lostAuthenticator: "Lost your authenticator?",
    reenroll: "Ask a SUPER_ADMIN to re-enroll",
    securitySummary: "RBAC enforced · 8h session · audited",
    missingFactor:
      "Two-factor required, but no active factor is enrolled. Ask a SUPER_ADMIN to enroll one for this account.",
    notInMfa: "Not in the two-factor step",
  },
  shell: {
    openNavigation: "Open navigation",
    operatorNavigation: "Operator navigation",
    websocket: "WebSocket: {{status}}",
    status: {
      open: "Connected",
      connecting: "Connecting",
      closed: "Disconnected",
    },
    superAdmin: "Super admin",
    myBalance: "My balance",
    accountAnd2fa: "Account & 2FA",
    signingOut: "Signing out…",
    signOut: "Sign out",
  },
  nav: {
    sections: {
      overview: "Overview",
      catalog: "Catalog",
      trading: "Trading",
      disputes: "Disputes",
      people: "People",
      treasury: "Treasury",
      brokers: "Brokers",
      cryptoWallets: "Crypto Wallets",
      engagement: "Engagement",
      operations: "Operations",
      ai: "AI",
      system: "System",
    },
    items: {
      dashboard: "Dashboard",
      markets: "Markets",
      languages: "Languages",
      eventOrdering: "Event ordering",
      activity: "Activity",
      activeOrders: "Active Orders",
      activePositions: "Active Positions",
      tradeHistory: "Trade History",
      orderHistory: "Order History",
      disputes: "Disputes",
      queue: "Queue",
      policy: "Policy",
      alerts: "Alerts",
      users: "Users",
      adminsRbac: "Admins & RBAC",
      admins: "Admins",
      rolesPermissions: "Roles & permissions",
      userWallets: "User Wallets",
      transfer: "Transfer",
      revenue: "Revenue",
      commissions: "Commissions",
      commissionPolicies: "Commission policies",
      distributions: "Distributions",
      adminPayouts: "Admin Payouts",
      systemAccounts: "System Accounts",
      receivables: "Receivables",
      brokerPayouts: "Payout batches",
      brokerDirectory: "Directory",
      brokerLifecycle: "Approvals & lifecycle",
      brokerIntegrations: "Integrations",
      brokerFunding: "Funding review",
      brokerRisk: "Risk",
      brokerSettlement: "Settlement",
      brokerReconciliation: "Reconciliation",
      brokerInvestigations: "Investigations",
      brokerRecoveryCases: "Recovery cases",
      currencies: "Currencies",
      conversionPools: "Conversion pools",
      fees: "Fees",
      policies: "Policies",
      userOverrides: "User overrides",
      configuration: "Configuration",
      network: "Network",
      assets: "Assets",
      assetRates: "Asset Rates",
      depositWithdrawal: "Deposit & Withdrawal",
      featureFlags: "Feature Flags",
      withdrawalPolicies: "Withdrawal Policies",
      gasFundingPolicies: "Gas Funding Policies",
      collectionPolicies: "Collection Policies",
      hotLiquidityPolicies: "Hot Liquidity Policies",
      transactions: "Transactions",
      deposits: "Deposits",
      withdrawals: "Withdrawals",
      custody: "Custody",
      wallets: "Wallets",
      hotLiquidity: "Hot Liquidity",
      gasFunding: "Gas funding",
      collections: "Collections",
      blacklist: "Blacklist",
      jobHealth: "Job health",
      campaigns: "Campaigns",
      leagues: "Leagues",
      globalLeague: "Global league",
      privateLeagues: "Private leagues",
      reconciliation: "Reconciliation",
      findings: "Findings",
      runs: "Runs",
      brokerIntegrity: "Broker integrity",
      aiAssistant: "AI Assistant",
      connections: "Connections",
      approvals: "Approvals",
      automation: "Automation",
      usage: "Usage",
      engineRing: "Engine ring",
      auditLog: "Audit log",
      settings: "Settings",
    },
  },
  dashboard: {
    title: "Operator dashboard",
    subtitle: "Snapshot across catalog, engine, treasury, users, and admin activity.",
    openMarkets: "Open markets",
    total: "{{count}} total",
    healthyPeers: "Healthy peers",
    instruments: "{{count}} instruments",
    activeUsers: "Active users",
    allTime: "all-time",
    openPositions: "Open positions",
    wallets: "{{count}} wallets",
    liveTape: "Markets — live tape",
    tapeMarket: "Market",
    tapeLast: "Last price",
    tapeBid: "Best bid",
    tapeAsk: "Best ask",
    noTickers: "No live tickers yet.",
    recentActivity: "Recent admin activity",
    viewAll: "view all",
    noAudit: "No audit events yet.",
  },
  errors: {
    accessDenied: "Access denied",
    accessDeniedDescription:
      "Your role doesn't include permission for this section. Ask a super admin to grant the required role, or use the sidebar to switch to a section your role covers.",
    sessionExpired: "Session expired",
    sessionExpiredDescription: "You've been signed out. Sign in again to continue.",
    notFound: "Not found",
    notFoundDescription: "The resource you requested doesn't exist or has been removed.",
    server: "Server error",
    serverDescription:
      "Something went wrong on our end. Try again in a moment — if it keeps happening, ping the on-call.",
    requestFailed: "Request failed",
    requestFailedDescription: "The request couldn't be completed.",
    network: "Network unreachable",
    networkDescription: "Couldn't reach the backoffice service. Check your connection and try again.",
    unexpected: "Something went wrong",
    unexpectedDescription: "An unexpected error occurred.",
    signInAgain: "Sign in again",
    backToDashboard: "Back to dashboard",
  },
  time: {
    ago: "{{value}} ago",
    in: "in {{value}}",
  },
  statuses: {
    DRAFT: "Draft",
    OPEN: "Open",
    CLOSED_PENDING_RESOLUTION: "Closing",
    RESOLVED: "Resolved",
    SETTLED: "Settled",
    CANCELED: "Canceled",
    ACTIVE: "Active",
    ARCHIVED: "Archived",
    SUSPENDED: "Suspended",
    CLOSED: "Closed",
    LOCKED: "Locked",
    DISABLED: "Disabled",
    FROZEN: "Frozen",
    PARTIALLY_FILLED: "Partially filled",
    FILLED: "Filled",
    PENDING: "Pending",
    NEW: "New",
    RESERVED: "Reserved",
    PENDING_ENGINE: "Pending engine",
    CANCEL_PENDING: "Cancel pending",
    REJECTED: "Rejected",
    EXPIRED: "Expired",
    FAILED: "Failed",
    CREDITED: "Credited",
    COMPLETED: "Completed",
    SUCCEEDED: "Succeeded",
    CONFIRMED: "Confirmed",
    CREDIT_FAILED: "Credit failed",
    ORPHANED: "Orphaned",
    SIGN_FAILED: "Signing failed",
    BROADCAST_FAILED: "Broadcast failed",
    REVERTED: "Reverted",
    DETECTED: "Detected",
    REQUESTED: "Requested",
  },
} as const;

type WidenStrings<T> = {
  readonly [K in keyof T]: T[K] extends string ? string : WidenStrings<T[K]>;
};

const th: WidenStrings<typeof en> = {
  common: {
    backoffice: "ระบบหลังบ้าน",
    language: "ภาษา",
    english: "English",
    thai: "ไทย",
    save: "บันทึก",
    cancel: "ยกเลิก",
    confirm: "ยืนยัน",
    close: "ปิด",
    edit: "แก้ไข",
    add: "เพิ่ม",
    delete: "ลบ",
    search: "ค้นหา",
    refresh: "รีเฟรช",
    retry: "ลองอีกครั้ง",
    loading: "กำลังโหลด…",
    dismiss: "ปิดการแจ้งเตือน",
    showPassword: "แสดงรหัสผ่าน",
    hidePassword: "ซ่อนรหัสผ่าน",
    previousPage: "หน้าก่อนหน้า",
    nextPage: "หน้าถัดไป",
    pagination: "การแบ่งหน้า",
    page: "หน้า {{page}}",
    pageOf: "หน้า {{page}} จาก {{total}}",
    showing: "แสดง {{from}}–{{to}} จาก {{total}} {{items}}",
    items: "รายการ",
    localDev: "นักพัฒนาในเครื่อง",
    create: "สร้าง",
    update: "อัปเดต",
    enabled: "เปิดใช้งาน",
    disabled: "ปิดใช้งาน",
    status: "สถานะ",
    name: "ชื่อ",
    code: "รหัส",
    order: "ลำดับ",
    actions: "การดำเนินการ",
    details: "รายละเอียด",
    filters: "ตัวกรอง",
    all: "ทั้งหมด",
    yes: "ใช่",
    no: "ไม่",
    none: "ไม่มี",
    copy: "คัดลอก",
    copied: "คัดลอกแล้ว",
    reset: "รีเซ็ต",
    apply: "นำไปใช้",
    clear: "ล้าง",
    itemLabels: {
      admins: "ผู้ดูแล",
      categories: "หมวดหมู่",
      changes: "การเปลี่ยนแปลง",
      comments: "ความคิดเห็น",
      deposits: "รายการฝาก",
      disputes: "ข้อพิพาท",
      entries: "รายการ",
      events: "อีเวนต์",
      keys: "คีย์",
      leagues: "ลีก",
      markets: "ตลาด",
      members: "สมาชิก",
      movements: "รายการเคลื่อนไหว",
      orders: "คำสั่งซื้อขาย",
      overrides: "ข้อยกเว้น",
      positions: "สถานะถือครอง",
      requests: "คำขอ",
      sources: "แหล่งที่มา",
      roles: "บทบาท",
      trades: "รายการซื้อขาย",
      transactions: "ธุรกรรม",
      versions: "เวอร์ชัน",
      wallets: "กระเป๋า",
      withdrawals: "รายการถอน",
    },
    sort: {
      asc: "จากน้อยไปมาก",
      desc: "จากมากไปน้อย",
      direction: "ทิศทางการเรียงลำดับ {{direction}}",
      title: "เรียงลำดับ{{direction}}",
    },
    selectNamed: "เลือก {{name}}",
    selectAllOnPage: "เลือก{{items}}ทั้งหมดในหน้านี้",
    selectedCount: "เลือกแล้ว {{count}} รายการ",
    publishCount: "เผยแพร่{{items}} {{count}} รายการหรือไม่?",
    unpublishCount: "ยกเลิกเผยแพร่{{items}} {{count}} รายการหรือไม่?",
    publishAll: "เผยแพร่ทั้งหมด",
    unpublishAll: "ยกเลิกเผยแพร่ทั้งหมด",
    filterSummary: {
      allStatuses: "ทุกสถานะ",
      openHolds: "รายการพักเงินที่เปิดอยู่",
      statusCount: "{{count}} สถานะ",
      stateCount: "{{count}} สถานะ",
      byStatus: "กรองตามสถานะ — {{summary}}",
      byState: "กรองตามสถานะ — {{summary}}",
    },
    counts: {
      activeLease_one: "สัญญาเช่าที่ใช้งาน {{count}} รายการ",
      activeLease_other: "สัญญาเช่าที่ใช้งาน {{count}} รายการ",
      instrument_one: "ตราสาร {{count}} รายการ",
      instrument_other: "ตราสาร {{count}} รายการ",
      coverage: "มีนโยบาย {{covered}} จาก {{total}} {{noun}}",
      paused: "หยุดชั่วคราว {{count}} รายการ",
    },
    policyNouns: {
      enabledNetwork_one: "เครือข่ายที่เปิดใช้งาน",
      enabledNetwork_other: "เครือข่ายที่เปิดใช้งาน",
      withdrawableAsset_one: "สินทรัพย์ที่ถอนได้",
      withdrawableAsset_other: "สินทรัพย์ที่ถอนได้",
      depositEnabledAsset_one: "สินทรัพย์ที่เปิดรับฝาก",
      depositEnabledAsset_other: "สินทรัพย์ที่เปิดรับฝาก",
    },
    noPolicy: "ไม่มีนโยบาย: {{names}}",
  },
  auth: {
    operatorConsole: "คอนโซลผู้ดูแล",
    signIn: "เข้าสู่ระบบ",
    subtitle: "ใช้อีเมลผู้ดูแลที่ได้รับมอบหมาย",
    email: "อีเมล",
    password: "รหัสผ่าน",
    signingIn: "กำลังเข้าสู่ระบบ",
    continue: "ดำเนินการต่อ",
    lostAccess: "ไม่สามารถเข้าถึงได้?",
    contactAdmin: "ติดต่อ SUPER_ADMIN",
    twoFactor: "การยืนยันสองขั้นตอน",
    enterCode: "กรอกรหัสยืนยัน",
    emailCode: "กรอกรหัส 6 หลักที่เราส่งไปยัง {{email}}",
    authenticatorCode:
      "เปิด {{factor}} บนอุปกรณ์ของ {{email}} แล้วกรอกรหัส 6 หลักปัจจุบัน",
    digit: "หลักที่ {{digit}} จาก 6",
    verifying: "กำลังตรวจสอบ",
    verifyAndSignIn: "ยืนยันและเข้าสู่ระบบ",
    backToPassword: "← กลับไปกรอกรหัสผ่าน",
    lostAuthenticator: "ไม่สามารถใช้แอปยืนยันตัวตนได้?",
    reenroll: "ขอให้ SUPER_ADMIN ลงทะเบียนใหม่",
    securitySummary: "บังคับใช้ RBAC · เซสชัน 8 ชม. · มีบันทึกตรวจสอบ",
    missingFactor:
      "ต้องยืนยันสองขั้นตอน แต่บัญชีนี้ยังไม่มีวิธียืนยันที่ใช้งานอยู่ โปรดขอให้ SUPER_ADMIN ลงทะเบียนให้",
    notInMfa: "ไม่ได้อยู่ในขั้นตอนการยืนยันสองขั้นตอน",
  },
  shell: {
    openNavigation: "เปิดเมนูนำทาง",
    operatorNavigation: "เมนูนำทางผู้ดูแล",
    websocket: "WebSocket: {{status}}",
    status: {
      open: "เชื่อมต่อแล้ว",
      connecting: "กำลังเชื่อมต่อ",
      closed: "ไม่ได้เชื่อมต่อ",
    },
    superAdmin: "ผู้ดูแลสูงสุด",
    myBalance: "ยอดคงเหลือของฉัน",
    accountAnd2fa: "บัญชีและ 2FA",
    signingOut: "กำลังออกจากระบบ…",
    signOut: "ออกจากระบบ",
  },
  nav: {
    sections: {
      overview: "ภาพรวม",
      catalog: "แค็ตตาล็อก",
      trading: "การซื้อขาย",
      disputes: "ข้อพิพาท",
      people: "บุคลากร",
      treasury: "คลัง",
      brokers: "โบรกเกอร์",
      cryptoWallets: "กระเป๋าคริปโต",
      engagement: "การมีส่วนร่วม",
      operations: "การปฏิบัติการ",
      ai: "AI",
      system: "ระบบ",
    },
    items: {
      dashboard: "แดชบอร์ด",
      markets: "ตลาด",
      languages: "ภาษา",
      eventOrdering: "ลำดับอีเวนต์",
      activity: "กิจกรรม",
      activeOrders: "คำสั่งซื้อขายที่ใช้งาน",
      activePositions: "สถานะถือครองที่ใช้งาน",
      tradeHistory: "ประวัติการซื้อขาย",
      orderHistory: "ประวัติคำสั่งซื้อขาย",
      disputes: "ข้อพิพาท",
      queue: "คิว",
      policy: "นโยบาย",
      alerts: "การแจ้งเตือน",
      users: "ผู้ใช้",
      adminsRbac: "ผู้ดูแลและ RBAC",
      admins: "ผู้ดูแล",
      rolesPermissions: "บทบาทและสิทธิ์",
      userWallets: "กระเป๋าผู้ใช้",
      transfer: "โอน",
      revenue: "รายได้",
      commissions: "ค่าคอมมิชชัน",
      commissionPolicies: "นโยบายค่าคอมมิชชัน",
      distributions: "การจัดสรร",
      adminPayouts: "การจ่ายเงินผู้ดูแล",
      systemAccounts: "บัญชีระบบ",
      receivables: "ยอดค้างรับ",
      brokerPayouts: "ชุดการจ่ายเงิน",
      brokerDirectory: "รายชื่อโบรกเกอร์",
      brokerLifecycle: "การอนุมัติและวงจรสถานะ",
      brokerIntegrations: "การเชื่อมต่อผู้ให้บริการ",
      brokerFunding: "ตรวจสอบเงินทุน",
      brokerRisk: "ความเสี่ยง",
      brokerSettlement: "การชำระยอดกับโบรกเกอร์",
      brokerReconciliation: "การกระทบยอด",
      brokerInvestigations: "การสืบค้นย้อนกลับ",
      brokerRecoveryCases: "เคสกู้คืนธุรกรรมผู้ให้บริการ",
      currencies: "สกุลเงิน",
      conversionPools: "แหล่งสภาพคล่องการแปลงสกุล",
      fees: "ค่าธรรมเนียม",
      policies: "นโยบาย",
      userOverrides: "ข้อยกเว้นผู้ใช้",
      configuration: "การกำหนดค่า",
      network: "เครือข่าย",
      assets: "สินทรัพย์",
      assetRates: "อัตราสินทรัพย์",
      depositWithdrawal: "การฝากและการถอน",
      featureFlags: "แฟล็กฟีเจอร์",
      withdrawalPolicies: "นโยบายการถอน",
      gasFundingPolicies: "นโยบายเติมค่าแก๊ส",
      collectionPolicies: "นโยบายรวบรวมเงิน",
      hotLiquidityPolicies: "นโยบายสภาพคล่อง Hot Wallet",
      transactions: "ธุรกรรม",
      deposits: "ฝาก",
      withdrawals: "ถอน",
      custody: "การดูแลสินทรัพย์",
      wallets: "กระเป๋า",
      hotLiquidity: "สภาพคล่อง Hot Wallet",
      gasFunding: "การเติมค่าแก๊ส",
      collections: "การรวบรวมเงิน",
      blacklist: "บัญชีดำ",
      jobHealth: "สถานะงาน",
      campaigns: "แคมเปญ",
      leagues: "ลีก",
      globalLeague: "ลีกทั่วโลก",
      privateLeagues: "ลีกส่วนตัว",
      reconciliation: "การกระทบยอด",
      findings: "ผลการตรวจสอบ",
      runs: "รอบการทำงาน",
      brokerIntegrity: "ความถูกต้องทางการเงินของโบรกเกอร์",
      aiAssistant: "ผู้ช่วย AI",
      connections: "การเชื่อมต่อ",
      approvals: "การอนุมัติ",
      automation: "ระบบอัตโนมัติ",
      usage: "การใช้งาน",
      engineRing: "วงแหวนเอนจิน",
      auditLog: "บันทึกตรวจสอบ",
      settings: "การตั้งค่า",
    },
  },
  dashboard: {
    title: "แดชบอร์ดผู้ดูแล",
    subtitle: "ภาพรวมแค็ตตาล็อก เอนจิน คลัง ผู้ใช้ และกิจกรรมผู้ดูแล",
    openMarkets: "ตลาดที่เปิดอยู่",
    total: "ทั้งหมด {{count}}",
    healthyPeers: "เพียร์ที่พร้อมใช้งาน",
    instruments: "{{count}} ตราสาร",
    activeUsers: "ผู้ใช้ที่ใช้งาน",
    allTime: "ตลอดเวลา",
    openPositions: "สถานะถือครองที่เปิดอยู่",
    wallets: "{{count}} กระเป๋า",
    liveTape: "ตลาด — ข้อมูลสด",
    tapeMarket: "ตลาด",
    tapeLast: "ราคาล่าสุด",
    tapeBid: "เสนอซื้อดีสุด",
    tapeAsk: "เสนอขายดีสุด",
    noTickers: "ยังไม่มีข้อมูลราคาสด",
    recentActivity: "กิจกรรมผู้ดูแลล่าสุด",
    viewAll: "ดูทั้งหมด",
    noAudit: "ยังไม่มีเหตุการณ์ตรวจสอบ",
  },
  errors: {
    accessDenied: "ไม่มีสิทธิ์เข้าถึง",
    accessDeniedDescription:
      "บทบาทของคุณไม่มีสิทธิ์สำหรับส่วนนี้ โปรดขอให้ผู้ดูแลสูงสุดมอบบทบาทที่ต้องใช้ หรือเลือกส่วนที่บทบาทของคุณเข้าถึงได้จากเมนูด้านข้าง",
    sessionExpired: "เซสชันหมดอายุ",
    sessionExpiredDescription: "คุณออกจากระบบแล้ว โปรดเข้าสู่ระบบอีกครั้งเพื่อดำเนินการต่อ",
    notFound: "ไม่พบข้อมูล",
    notFoundDescription: "ไม่มีทรัพยากรที่ร้องขอหรือถูกนำออกแล้ว",
    server: "เซิร์ฟเวอร์ขัดข้อง",
    serverDescription: "ระบบของเรามีปัญหา โปรดลองอีกครั้งในอีกสักครู่ หากยังเกิดซ้ำให้แจ้งผู้ดูแลเวร",
    requestFailed: "คำขอล้มเหลว",
    requestFailedDescription: "ไม่สามารถดำเนินการตามคำขอได้",
    network: "ไม่สามารถเชื่อมต่อเครือข่าย",
    networkDescription: "ไม่สามารถเชื่อมต่อบริการระบบหลังบ้าน ตรวจสอบการเชื่อมต่อแล้วลองอีกครั้ง",
    unexpected: "เกิดข้อผิดพลาด",
    unexpectedDescription: "เกิดข้อผิดพลาดที่ไม่คาดคิด",
    signInAgain: "เข้าสู่ระบบอีกครั้ง",
    backToDashboard: "กลับไปแดชบอร์ด",
  },
  time: {
    ago: "{{value}} ที่แล้ว",
    in: "ในอีก {{value}}",
  },
  statuses: {
    DRAFT: "ร่าง",
    OPEN: "เปิดอยู่",
    CLOSED_PENDING_RESOLUTION: "กำลังปิด",
    RESOLVED: "ตัดสินผลแล้ว",
    SETTLED: "ชำระแล้ว",
    CANCELED: "ยกเลิกแล้ว",
    ACTIVE: "ใช้งานอยู่",
    ARCHIVED: "เก็บถาวรแล้ว",
    SUSPENDED: "ระงับ",
    CLOSED: "ปิดแล้ว",
    LOCKED: "ล็อกแล้ว",
    DISABLED: "ปิดใช้งาน",
    FROZEN: "ถูกตรึง",
    PARTIALLY_FILLED: "จับคู่บางส่วน",
    FILLED: "จับคู่ครบ",
    PENDING: "รอดำเนินการ",
    NEW: "ใหม่",
    RESERVED: "สำรองแล้ว",
    PENDING_ENGINE: "รอเอนจิน",
    CANCEL_PENDING: "รอยกเลิก",
    REJECTED: "ถูกปฏิเสธ",
    EXPIRED: "หมดอายุ",
    FAILED: "ล้มเหลว",
    CREDITED: "บันทึกเครดิตแล้ว",
    COMPLETED: "เสร็จสิ้น",
    SUCCEEDED: "สำเร็จ",
    CONFIRMED: "ยืนยันแล้ว",
    CREDIT_FAILED: "บันทึกเครดิตล้มเหลว",
    ORPHANED: "ขาดการเชื่อมโยง",
    SIGN_FAILED: "ลงนามล้มเหลว",
    BROADCAST_FAILED: "เผยแพร่ธุรกรรมล้มเหลว",
    REVERTED: "ถูกย้อนกลับ",
    DETECTED: "ตรวจพบแล้ว",
    REQUESTED: "ร้องขอแล้ว",
  },
};

function isAppLocale(value: string | null | undefined): value is AppLocale {
  return value === "en" || value === "th";
}

function initialLocale(): AppLocale {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  return isAppLocale(stored) ? stored : "en";
}

const startupLocale = initialLocale();
const initialization = i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    th: { translation: th },
  },
  lng: startupLocale,
  fallbackLng: "en",
  supportedLngs: [...APP_LOCALES],
  interpolation: { escapeValue: false },
  returnNull: false,
});

let pageCopyPromise: Promise<void> | null = null;
let pageCopyKeysByEnglish: Map<string, string> | null = null;

/** Load the large route/component catalog only when Thai is actually used. */
export function ensurePageCopyResources(): Promise<void> {
  if (pageCopyPromise) return pageCopyPromise;
  pageCopyPromise = Promise.all([
    import("./pageCopy.en.json"),
    import("./pageCopy.th.json"),
  ]).then(([english, thai]) => {
    pageCopyKeysByEnglish = new Map(
      Object.entries(english.default).map(([key, value]) => [
        value.replace(/\s+/g, " ").trim(),
        key,
      ]),
    );
    i18n.addResourceBundle(
      "en",
      "translation",
      { pageCopy: english.default },
      true,
      true,
    );
    i18n.addResourceBundle(
      "th",
      "translation",
      { pageCopy: thai.default },
      true,
      true,
    );
  });
  return pageCopyPromise;
}

export async function changeAppLocale(locale: AppLocale) {
  await initialization;
  if (locale === "th") await ensurePageCopyResources();
  await i18n.changeLanguage(locale);
}

/** Translate source-owned runtime copy that is passed through component props. */
export function translatePageCopy(source: string): string {
  if (currentLocale() !== "th") return source;
  const key = pageCopyKeysByEnglish?.get(source.replace(/\s+/g, " ").trim());
  return key ? i18n.t(`pageCopy.${key}`) : source;
}

export const i18nReady = initialization.then(async () => {
  if (startupLocale === "th") await ensurePageCopyResources();
});

function applyLocale(locale: AppLocale) {
  if (typeof document !== "undefined") document.documentElement.lang = locale;
  if (typeof window !== "undefined") {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }
}

applyLocale(startupLocale);
i18n.on("languageChanged", (value) => {
  applyLocale(isAppLocale(value) ? value : "en");
});

export function currentLocale(): AppLocale {
  return isAppLocale(i18n.resolvedLanguage) ? i18n.resolvedLanguage : "en";
}

export function intlLocale(locale = currentLocale()): string {
  return locale === "th" ? "th-TH" : "en-US";
}

export default i18n;
